import { PublicClientApplication, EventType, AuthenticationResult } from '@azure/msal-browser';
import { isAzureAdConfigured, msalConfig } from './msalConfig';

// Single shared MSAL instance for the whole app. Both the `MsalProvider` in
// `App.tsx` and the axios interceptor in `services/api.ts` must use this
// instance so they share the same token cache and account state.
export const msalInstance = isAzureAdConfigured
  ? new PublicClientApplication(msalConfig)
  : null;

// MSAL v3 requires an explicit initialize() before any other API call. Without
// this, the redirect-back flow can't process the auth code from the URL and
// the user gets bounced back into the login redirect loop.
//
// We also process the redirect promise here so the auth code from the URL is
// consumed exactly once and accounts are populated before React renders.
export async function bootstrapMsal(): Promise<void> {
  if (!msalInstance) return;

  await msalInstance.initialize();

  const result = await msalInstance.handleRedirectPromise();
  if (result?.account) {
    msalInstance.setActiveAccount(result.account);
  } else {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  // Keep the active account in sync with future login events.
  msalInstance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
    ) {
      const payload = event.payload as AuthenticationResult | undefined;
      if (payload?.account) {
        msalInstance.setActiveAccount(payload.account);
      }
    }
  });
}
